// @ts-nocheck
/**
 * Trial Signup Wizard Component - Multi-step form for trial registration
 * Epic 18: High-Performance Marketing Site
 */

import React, { useState } from 'react';
import {
  Form,
  FormGroup,
  FluidTextInput,
  FluidSelect,
  SelectItem,
  Button,
  ProgressIndicator,
  ProgressStep,
  InlineNotification,
  Checkbox,
  DatePicker,
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  Loading,
} from '@carbon/react';
import { ArrowRight, ArrowLeft } from '@carbon/icons-react';
import styles from './TrialSignupWizard.module.scss';

interface TrialSignupWizardProps {
  onComplete: () => void;
  source: string;
}

interface SignupData {
  name: string;
  email: string;
  institution: string;
  role: string;
  institutionType: string;
  phone?: string;
  preferredContactDate: string;
  preferredContactTime: string;
  acceptTerms: boolean;
}

const TrialSignupWizard: React.FC<TrialSignupWizardProps> = ({ onComplete, source }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<SignupData>({
    name: '',
    email: '',
    institution: '',
    role: '',
    institutionType: '',
    phone: '',
    preferredContactDate: '',
    preferredContactTime: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<Partial<SignupData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const steps = [
    { label: 'Personal Information', description: 'Your name and email' },
    { label: 'Institution Details', description: 'Your school or organization' },
    { label: 'Contact Preferences', description: 'When to reach you' },
    { label: 'Confirmation', description: 'Review and submit' },
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<SignupData> = {};

    switch (step) {
      case 0:
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Invalid email format';
        }
        break;
      case 1:
        if (!formData.institution) newErrors.institution = 'Institution is required';
        if (!formData.role) newErrors.role = 'Role is required';
        if (!formData.institutionType) newErrors.institutionType = 'Institution type is required';
        break;
      case 2:
        // Contact preferences step - no required fields
        break;
      case 3:
        if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const visitorId =
        localStorage.getItem('visitorId') || `v-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const response = await fetch('/api/marketing/leads/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source,
          visitorId,
        }),
      });

      if (!response.ok) {
        // Handle HTTP errors
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try again.');
        } else if (response.status === 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      }

      const result = await response.json();

      if (result.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onComplete();
        }, 2000);
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit. Please try again.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: keyof SignupData, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  if (submitSuccess) {
    return (
      <div className={styles.successContainer}>
        <InlineNotification
          kind="success"
          title="Success!"
          subtitle="Your trial account has been created. Check your email for next steps."
          hideCloseButton
        />
      </div>
    );
  }

  return (
    <div className="trial-signup-wizard">
      <ProgressIndicator currentIndex={currentStep} spaceEqually>
        {steps.map((step, index) => (
          <ProgressStep key={index} label={step.label} description={step.description} />
        ))}
      </ProgressIndicator>

      {submitError && (
        <InlineNotification
          kind="error"
          title="Submission Error"
          subtitle={submitError}
          onCloseButtonClick={() => setSubmitError(null)}
          lowContrast
        />
      )}

      <Form
        className={styles.formContainer}
        onSubmit={e => {
          e.preventDefault();
          handleNext();
        }}
      >
        {currentStep === 0 && (
          <>
            <FormGroup legendText="">
              <FluidTextInput
                id="name"
                labelText="Full Name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={e => updateFormData('name', e.target.value)}
                invalid={!!errors.name}
                invalidText={errors.name}
                required
              />
            </FormGroup>
            <FormGroup legendText="">
              <FluidTextInput
                id="email"
                labelText="Email Address"
                placeholder="your.email@example.com"
                type="email"
                value={formData.email}
                onChange={e => updateFormData('email', e.target.value)}
                invalid={!!errors.email}
                invalidText={errors.email}
                required
              />
            </FormGroup>
            <FormGroup legendText="">
              <FluidTextInput
                id="phone"
                labelText="Phone Number (Optional)"
                placeholder="+1 (555) 123-4567"
                type="tel"
                value={formData.phone}
                onChange={e => updateFormData('phone', e.target.value)}
                helperText="We'll use this for account recovery"
              />
            </FormGroup>
          </>
        )}

        {currentStep === 1 && (
          <>
            <FormGroup legendText="">
              <FluidTextInput
                id="institution"
                labelText="Institution Name"
                placeholder="Enter your school or organization name"
                value={formData.institution}
                onChange={e => updateFormData('institution', e.target.value)}
                invalid={!!errors.institution}
                invalidText={errors.institution}
                required
              />
            </FormGroup>
            <FormGroup legendText="">
              <FluidSelect
                id="role"
                labelText="Your Role"
                value={formData.role}
                onChange={e => updateFormData('role', e.target.value)}
                invalid={!!errors.role}
                invalidText={errors.role}
                required
              >
                <SelectItem value="" text="Select your role" />
                <SelectItem value="teacher" text="Teacher" />
                <SelectItem value="principal" text="Principal" />
                <SelectItem value="administrator" text="Administrator" />
                <SelectItem value="parent" text="Parent" />
                <SelectItem value="other" text="Other" />
              </FluidSelect>
            </FormGroup>
            <FormGroup legendText="">
              <FluidSelect
                id="institutionType"
                labelText="Institution Type"
                value={formData.institutionType}
                onChange={e => updateFormData('institutionType', e.target.value)}
                invalid={!!errors.institutionType}
                invalidText={errors.institutionType}
                required
              >
                <SelectItem value="" text="Select institution type" />
                <SelectItem value="elementary" text="Elementary School" />
                <SelectItem value="middle_school" text="Middle School" />
                <SelectItem value="high_school" text="High School" />
                <SelectItem value="district" text="School District" />
                <SelectItem value="private_school" text="Private School" />
              </FluidSelect>
            </FormGroup>
          </>
        )}

        {currentStep === 2 && (
          <>
            <FormGroup legendText="">
              <DatePicker datePickerType="single" value={formData.preferredContactDate}>
                <DatePickerInput
                  id="preferredContactDate"
                  placeholder="mm/dd/yyyy"
                  labelText="Preferred Contact Date (Optional)"
                  value={formData.preferredContactDate}
                  onChange={e => updateFormData('preferredContactDate', e.target.value)}
                  helperText="When would you like us to reach out?"
                />
              </DatePicker>
            </FormGroup>
            <FormGroup legendText="">
              <TimePicker
                id="preferredContactTime"
                labelText="Preferred Contact Time (Optional)"
                value={formData.preferredContactTime}
                onChange={e => updateFormData('preferredContactTime', e.target.value)}
                helperText="Best time to contact you"
              >
                <TimePickerSelect id="time-picker-select-1">
                  <SelectItem value="" text="Select time" />
                  <SelectItem value="09:00" text="9:00 AM" />
                  <SelectItem value="10:00" text="10:00 AM" />
                  <SelectItem value="11:00" text="11:00 AM" />
                  <SelectItem value="12:00" text="12:00 PM" />
                  <SelectItem value="13:00" text="1:00 PM" />
                  <SelectItem value="14:00" text="2:00 PM" />
                  <SelectItem value="15:00" text="3:00 PM" />
                  <SelectItem value="16:00" text="4:00 PM" />
                  <SelectItem value="17:00" text="5:00 PM" />
                </TimePickerSelect>
              </TimePicker>
            </FormGroup>
          </>
        )}

        {currentStep === 3 && (
          <div>
            <div className={styles.reviewSection}>
              <h4 className={styles.reviewTitle}>Review Your Information</h4>
              <p className={styles.reviewItem}>
                <strong>Name:</strong> {formData.name}
              </p>
              <p className={styles.reviewItem}>
                <strong>Email:</strong> {formData.email}
              </p>
              <p className={styles.reviewItem}>
                <strong>Institution:</strong> {formData.institution}
              </p>
              <p className={styles.reviewItem}>
                <strong>Role:</strong> {formData.role}
              </p>
              <p className={styles.reviewItem}>
                <strong>Type:</strong> {formData.institutionType}
              </p>
              {formData.preferredContactDate && (
                <p className={styles.reviewItem}>
                  <strong>Preferred Contact:</strong> {formData.preferredContactDate} at{' '}
                  {formData.preferredContactTime || 'any time'}
                </p>
              )}
            </div>

            <FormGroup legendText="">
              <Checkbox
                id="acceptTerms"
                labelText="I accept the Terms of Service and Privacy Policy"
                checked={formData.acceptTerms}
                onChange={(event, { checked }) => updateFormData('acceptTerms', checked)}
                invalid={!!errors.acceptTerms}
                invalidText={errors.acceptTerms}
              />
            </FormGroup>
          </div>
        )}

        <div className={styles.buttonContainer}>
          {currentStep > 0 && (
            <Button kind="secondary" renderIcon={ArrowLeft} onClick={handleBack} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button
            kind="primary"
            renderIcon={ArrowRight}
            onClick={handleNext}
            disabled={isSubmitting}
            className={currentStep === 0 ? styles.nextButton : undefined}
          >
            {currentStep === steps.length - 1 ? (
              isSubmitting ? (
                <>
                  <Loading small withOverlay={false} />
                  Submitting...
                </>
              ) : (
                'Start Free Trial'
              )
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TrialSignupWizard;
